import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, EyeOff, Eye } from 'lucide-react';
import { Pagination } from '@/components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReviewModeration = () => {
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlyHidden, setOnlyHidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/admin/marketplace/reviews?page=${page}&per_page=25&only_hidden=${onlyHidden}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviews(res.data.reviews || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Could not load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, onlyHidden]);

  const toggleHidden = async (review) => {
    const hiding = !review.is_hidden;
    let reason = null;
    if (hiding) {
      reason = window.prompt('Why is this review being hidden? (kept for your records)', 'Breaches community guidelines');
      if (reason === null) return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/marketplace/reviews/${review.id}/${hiding ? 'hide' : 'unhide'}`,
        hiding ? { reason } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(hiding ? 'Review hidden and removed from ratings' : 'Review restored');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-review-moderation">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-gray-600">
          {total} review{total !== 1 ? 's' : ''}{onlyHidden ? ' hidden' : ''}. Hiding a review removes it from
          the storefront, the product page and every star rating. It is never deleted.
        </p>
        <Button
          variant={onlyHidden ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setOnlyHidden(v => !v); setPage(1); }}
          data-testid="review-filter-hidden-btn"
        >
          {onlyHidden ? 'Showing hidden only' : 'Show hidden only'}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="no-reviews-msg">No reviews to show.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              className="border rounded-lg p-4 bg-white"
              data-testid={`admin-review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          className="w-3.5 h-3.5"
                          color="#b07010"
                          fill={n <= (r.rating || 0) ? '#b07010' : 'none'}
                        />
                      ))}
                    </span>
                    {r.is_hidden && (
                      <Badge className="bg-red-500 text-white text-xs" data-testid="review-hidden-badge">
                        Hidden
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-900 font-medium">
                    {r.product_title || (r.booking_id ? 'Session review' : 'Storefront review')}
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    {r.vendor_store_name || 'Unknown storefront'} · by {r.customer_name || 'Member'}
                    {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ''}
                  </p>
                  {r.body && <p className="text-sm text-gray-700 whitespace-pre-line">{r.body}</p>}
                  {r.vendor_reply && (
                    <p className="text-sm text-gray-600 mt-2 pl-3 border-l-2 border-gray-200">
                      <strong>Vendor reply:</strong> {r.vendor_reply}
                    </p>
                  )}
                  {r.is_hidden && r.hidden_reason && (
                    <p className="text-xs text-red-600 mt-2">Hidden: {r.hidden_reason}</p>
                  )}
                </div>
                <Button
                  variant={r.is_hidden ? 'outline' : 'destructive'}
                  size="sm"
                  onClick={() => toggleHidden(r)}
                  data-testid={`review-toggle-hidden-${r.id}`}
                >
                  {r.is_hidden ? (
                    <><Eye className="w-4 h-4 mr-1" /> Restore</>
                  ) : (
                    <><EyeOff className="w-4 h-4 mr-1" /> Hide</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          theme="light"
          testidPrefix="admin-reviews"
        />
      )}
    </div>
  );
};

export default AdminReviewModeration;
